import React, { useEffect, useState } from 'react';
import { MenuIcon, BellIcon, SearchIcon } from 'lucide-react';
import { Link } from 'react-router-dom';
interface HeaderProps {
  toggleSidebar: () => void;
  onLogout: () => void;
}
const Header: React.FC<HeaderProps> = ({
  toggleSidebar,
  onLogout
}) => {
  const [showUserMenu, setShowUserMenu] = useState(false);
  const [showNotifications, setShowNotifications] = useState(false);
  const [currentTime, setCurrentTime] = useState(new Date());
  const [searchQuery, setSearchQuery] = useState('');
  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentTime(new Date());
    }, 1000);
    return () => clearInterval(timer);
  }, []);
  const toggleUserMenu = () => {
    setShowUserMenu(!showUserMenu);
    if (showNotifications) setShowNotifications(false);
  };
  const toggleNotifications = () => {
    setShowNotifications(!showNotifications);
    if (showUserMenu) setShowUserMenu(false);
  };
  const formatTime = (date: Date) => {
    return new Intl.DateTimeFormat('en-US', {
      hour: 'numeric',
      minute: '2-digit',
      hour12: true,
      timeZoneName: 'short'
    }).format(date);
  };
  return <header className="bg-white sticky top-0 z-30 border-b border-gray-100" data-id="element-35">
      <div className="px-4" data-id="element-36">
        <div className="flex h-16 items-center gap-4" data-id="element-37">
          {/* Left section with search */}
          <div className="flex items-center flex-1" data-id="element-38">
            <button onClick={toggleSidebar} className="md:hidden -ml-1 p-2 rounded-md text-gray-500 hover:text-gray-900 hover:bg-gray-100 focus:outline-none focus:ring-2 focus:ring-inset focus:ring-primary-500" aria-label="Toggle sidebar" data-id="element-39">
              <MenuIcon size={24} data-id="element-40" />
            </button>
            <div className="relative w-full max-w-xl" data-id="element-41">
              <div className="pointer-events-none absolute inset-y-0 left-0 flex items-center pl-3" data-id="element-42">
                <SearchIcon size={16} className="text-gray-400" data-id="element-43" />
              </div>
              <input type="text" placeholder="Search shipments, locations, or tracking numbers..." value={searchQuery} onChange={e => setSearchQuery(e.target.value)} className="block w-full pl-10 pr-3 py-2 border border-gray-200 rounded-lg text-sm text-gray-900 placeholder:text-gray-500 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500" data-id="element-44" />
            </div>
          </div>
          {/* Right section */}
          <div className="flex items-center gap-4" data-id="element-45">
            {/* Current Time */}
            <div className="hidden md:block text-sm text-gray-500" data-id="element-46">
              {formatTime(currentTime)}
            </div>
            {/* Notifications */}
            <div className="relative" data-id="element-47">
              <button onClick={toggleNotifications} className="relative p-2 rounded-full text-gray-500 hover:text-gray-900 hover:bg-gray-100 focus:outline-none focus:ring-2 focus:ring-primary-500" aria-label={`Notifications ${showNotifications ? '(open)' : '(closed)'}`} data-id="element-48">
                <BellIcon size={20} data-id="element-49" />
                <span className="absolute top-1.5 right-1.5 h-2.5 w-2.5 rounded-full bg-primary-600 ring-2 ring-white" data-id="element-50" />
              </button>
              {showNotifications && <div className="absolute right-0 mt-2 w-80 rounded-lg bg-white py-1 shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none" data-id="element-51">
                  <div className="px-4 py-2 border-b border-gray-100" data-id="element-52">
                    <h3 className="text-sm font-semibold text-gray-900" data-id="element-53">
                      Notifications
                    </h3>
                  </div>
                  <div className="mt-1 px-1 py-1 max-h-96 overflow-y-auto" data-id="element-54">
                    {[1, 2, 3].map(notification => <button key={notification} className="block w-full px-3 py-2 text-left rounded-md hover:bg-gray-50" data-id="element-55">
                        <p className="text-sm text-gray-900" data-id="element-56">
                          Shipment #12345 has been delivered
                        </p>
                        <p className="text-xs text-gray-500 mt-0.5" data-id="element-57">
                          10 minutes ago
                        </p>
                      </button>)}
                  </div>
                </div>}
            </div>
            {/* User menu */}
            <div className="relative" data-id="element-58">
              <button onClick={toggleUserMenu} className="flex items-center gap-3 rounded-full p-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 hover:bg-gray-50" aria-label={`User menu ${showUserMenu ? '(open)' : '(closed)'}`} data-id="element-59">
                <img src="https://images.unsplash.com/photo-1472099645785-5658abf4ff4e?ixlib=rb-1.2.1&ixid=eyJhcHBfaWQiOjEyMDd9&auto=format&fit=facearea&facepad=2&w=256&h=256&q=80" alt="" className="h-8 w-8 rounded-full ring-2 ring-white" data-id="element-60" />
                <div className="hidden md:block text-left" data-id="element-61">
                  <p className="text-sm font-medium text-gray-700" data-id="element-62">
                    John Smith
                  </p>
                  <p className="text-xs text-gray-500" data-id="element-63">Administrator</p>
                </div>
              </button>
              {showUserMenu && <div className="absolute right-0 mt-2 w-56 rounded-lg bg-white py-1 shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none" data-id="element-64">
                  <div className="px-4 py-2 border-b border-gray-100" data-id="element-65">
                    <p className="text-sm font-medium text-gray-900" data-id="element-66">
                      John Smith
                    </p>
                    <p className="text-xs text-gray-500 mt-0.5" data-id="element-67">
                      john@example.com
                    </p>
                  </div>
                  <Link to="/profile" className="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-50" data-id="element-68">
                    Your Profile
                  </Link>
                  <Link to="/settings" className="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-50" data-id="element-69">
                    Settings
                  </Link>
                  <button onClick={onLogout} className="block w-full px-4 py-2 text-left text-sm text-gray-700 hover:bg-gray-50" data-id="element-70">
                    Sign out
                  </button>
                </div>}
            </div>
          </div>
        </div>
      </div>
    </header>;
};
export default Header;